
import React, { useEffect, useState } from 'react';
import {
  Typography, Box, styled, Card, CardContent, Fade, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, TablePagination, IconButton, Select, MenuItem, FormControl, InputLabel, Button,
  Collapse, CircularProgress, Alert,
  Backdrop,
  Modal,
} from '@mui/material';
import { TextField, InputAdornment } from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import CloseIcon from '@mui/icons-material/Close';
import { getClientData, getClientBalance, getDepositHistory } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import DepositForm from './DepositForm';
// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, ArcElement, Title, Tooltip, Legend);

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.MuiTableCell-head`]: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
    fontWeight: 700,
    fontSize: 15,
    padding: theme.spacing(2),
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  [`&.MuiTableCell-body`]: {
    fontSize: 14,
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}40`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.light + '20',
    transition: 'background-color 0.3s ease',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
  },
}));

const ExpandButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

// API calls
const getClientUserBalanceTransactions = async (clientUserId: number) => {
  const response = await fetch(`https://games.ibitplay.com/api/admin/client-user/balance-transactions?client_user_id=${clientUserId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  return response.json();
};

const getClientBalanceTransactions = async (clientId: number) => {
  const response = await fetch(`https://games.ibitplay.com/api/admin/client/balance-transactions?client_id=${clientId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  return response.json();
};

const getClientUserBettingStats = async (clientId: number) => {
  const response = await fetch(`https://games.ibitplay.com/api/admin/client-user/betting-stats?client_id=${clientId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  return response.json();
};

const ClientView: React.FC = () => {
  const [clientData, setClientData] = useState<any>(null);
  const [balance, setBalance] = useState<any>(null);
  const [clientTransactions, setClientTransactions] = useState<any[]>([]);
  const [bettingStats, setBettingStats] = useState<any>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [userTransactions, setUserTransactions] = useState<{ [key: number]: any[] }>({});
  const [showClientTransactions, setShowClientTransactions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showDepositForm, setShowDepositForm] = useState(false);
  const [depositHistory, setDepositHistory] = useState<any[]>([]);
  const [showDepositHistory, setShowDepositHistory] = useState(false);
const [searchTerm, setSearchTerm] = useState('');
const handleSearchChange = (event:any) => {
  setSearchTerm(event.target.value);
  setPage(0); // Reset to first page when searching
};

  useEffect(() => {
    fetchInitialData();
    if (clientData?.client?.id) {
      fetchDepositHistory(clientData.client.id);
    }
  }, [clientData?.client?.id]);

  const handleOpenHistory = async () => {
    if (!showDepositHistory && clientData?.client?.id) {
      await fetchDepositHistory(clientData.client.id);
    }
    setShowDepositHistory(!showDepositHistory);
  };

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch client data and balance first
      const [dataResponse, balanceResponse] = await Promise.all([
        getClientData(),
        getClientBalance(),
      ]);
      
      const clientDataResult = dataResponse.data.data;
      setClientData(clientDataResult);
      setBalance(balanceResponse.data.data);

      // Only fetch transactions and stats if client ID is available
      if (clientDataResult?.client?.id) {
        const [clientTxResponse, statsResponse] = await Promise.all([
          getClientBalanceTransactions(clientDataResult.client.id),
          getClientUserBettingStats(clientDataResult.client.id),
        ]);
        
        setClientTransactions(clientTxResponse.data);
        setBettingStats(statsResponse.data);
      }
    } catch (err) {
      console.error('Failed to fetch data', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const fetchDepositHistory = async (clientId: number) => {
    try {
      const response = await getDepositHistory(clientId);
      setDepositHistory(response.data.data);
    } catch (err) {
      console.error('Failed to fetch deposit history', err);
      setError('Failed to load deposit history.');
    }
  };
  const fetchUserTransactions = async (clientUserId: number) => {
    try {
      const response = await getClientUserBalanceTransactions(clientUserId);
      setUserTransactions(prev => ({
        ...prev,
        [clientUserId]: response.data
      }));
    } catch (err) {
      console.error('Failed to fetch user transactions', err);
      setError('Failed to load user transactions.');
    }
  };

  const handleUserFilterChange = (event: any) => {
    setSelectedUserId(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleUserTransactions = (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
    } else {
      setExpandedUserId(userId);
      if (!userTransactions[userId]) {
        fetchUserTransactions(userId);
      }
    }
  };

  // Chart Data for Betting Stats (Pie Chart)
  const bettingStatsChartData = {
    labels: ['Total Bets', 'Total Wins', 'Total Losses'],
    datasets: [
      {
        data: bettingStats ? [
          bettingStats.USDT.total_bet + bettingStats.INR.total_bet,
          bettingStats.USDT.total_win + bettingStats.INR.total_win,
          bettingStats.USDT.total_loss + bettingStats.INR.total_loss
        ] : [0, 0, 0],
        backgroundColor: ['#3f51b5', '#f50057', '#4caf50'],
        borderColor: '#ffffff',
        borderWidth: 2,
      },
    ],
  };

  // Chart Data for User Balance Transactions
  const getUserBalanceChartData = (userId: number) => ({
    labels: userTransactions[userId]?.map((tx) => new Date(tx.created_at).toLocaleDateString()) || [],
    datasets: [
      {
        label: 'Balance After (USDT)',
        data: userTransactions[userId]?.map((tx) => tx.currency === 'USDT' ? tx.balance_after : 0) || [],
        borderColor: '#3f51b5',
        fill: false,
      },
      {
        label: 'Balance After (INR)',
        data: userTransactions[userId]?.map((tx) => tx.currency === 'INR' ? tx.balance_after : 0) || [],
        borderColor: '#f50057',
        fill: false,
      },
    ],
  });

  // Chart Data for Client Balance Transactions
  const clientBalanceChartData = {
    labels: clientTransactions.map((tx) => new Date(tx.created_at).toLocaleDateString()),
    datasets: [
      {
        label: 'Balance After (USD)',
        data: clientTransactions.map((tx) => tx.balance_after),
        borderColor: '#3f51b5',
        fill: false,
      },
    ],
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5', width: '100%' }}>

      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          mt: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
     <StyledCard>
  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3 }}>
    <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', fontSize: { xs: 24, sm: 32 } }}>
      Client Dashboard
    </Typography>
    <Box sx={{ display: 'flex', gap: 2 }}>
    <Button
        variant="contained"
        onClick={() => setShowDepositForm(true)}
        sx={{ borderRadius: 1, textTransform: 'none', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, px: 3, py: 1 }}
      >
        Deposit
      </Button>
      <Button
  variant="contained"
  onClick={handleOpenHistory}// Changed to open deposit history modal
  sx={{ borderRadius: 1, textTransform: 'none', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, px: 3, py: 1 }}
>
  Deposit History
</Button>
      <IconButton
        onClick={fetchInitialData}
        sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 1, p: 1.5 }}
        title="Refresh Data"
      >
        <RefreshIcon />
      </IconButton>
    </Box>
  </CardContent>
</StyledCard>
<Modal
  open={showDepositHistory}
  onClose={() => handleOpenHistory}
  closeAfterTransition
  slots={{ backdrop: Backdrop }}
  slotProps={{
    backdrop: {
      timeout: 500,
    },
  }}
>
  <Fade in={showDepositHistory}>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '80%', md: 800 },
        maxHeight: '90vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        p: 4,
      }}
    >
      <IconButton
        aria-label="close"
        onClick={() => setShowDepositHistory(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1a237e' }}>
        Deposit History
      </Typography>
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Amount</StyledTableCell>
              <StyledTableCell>Currency</StyledTableCell>
              <StyledTableCell>Chain</StyledTableCell>
              <StyledTableCell>Deposit Address</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Created At</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {depositHistory.map((deposit) => (
              <StyledTableRow key={deposit.id}>
                <StyledTableCell>{deposit.amount}</StyledTableCell>
                <StyledTableCell>{deposit.currency}</StyledTableCell>
                <StyledTableCell>{deposit.chain}</StyledTableCell>
                <StyledTableCell>{deposit.deposit_address}</StyledTableCell>
                <StyledTableCell>{deposit.status}</StyledTableCell>
                <StyledTableCell>{new Date(deposit.created_at).toLocaleString()}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  </Fade>
</Modal>
<Modal
  open={showDepositForm}
  onClose={() => setShowDepositForm(false)}
  closeAfterTransition
  slots={{ backdrop: Backdrop }}
  slotProps={{
    backdrop: {
      timeout: 500,
    },
  }}
>
  <Fade in={showDepositForm}>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '80%', md: 600 },
        maxHeight: '90vh',
        overflowY: 'auto',
        bgcolor: 'transparent', // Transparent to let ModernCard's background show
        boxShadow: 24,
        borderRadius: 2,
        p: 0, // No padding, as ModernCard handles it

      }}
    >
      {/* Close button with icon at top right */}
      <IconButton
        aria-label="close"
        onClick={() => setShowDepositForm(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 10,
          bgcolor: 'background.paper',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {clientData?.client?.id && <DepositForm clientId={clientData.client.id} />}
     
    </Box>
  </Fade>
</Modal>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Client Information */}
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: 18, sm: 20 }, color: '#1a237e' }}>
              Client Information
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <Typography sx={{ fontSize: 16 }}><strong>Name:</strong> {clientData?.client?.client_name}</Typography>
              <Typography sx={{ fontSize: 16 }}><strong>Balance:</strong> {clientData?.client?.balance} USD</Typography>
              <Typography sx={{ fontSize: 16 }}><strong>API Key:</strong> {clientData?.client?.api_key}</Typography>
              <Typography sx={{ fontSize: 16 }}><strong>API Secret:</strong> {clientData?.client?.api_secret}</Typography>
              <Typography sx={{ fontSize: 16 }}><strong>Status:</strong> {clientData?.client?.status}</Typography>
              <Typography sx={{ fontSize: 16 }}><strong>GGR %:</strong> {clientData?.client?.ggr_percentage} Approx</Typography>
            </Box>
          </CardContent>
        </StyledCard>

        {/* Betting Statistics */}
        {bettingStats && (
          <StyledCard>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: 18, sm: 20 }, color: '#1a237e' }}>
                Betting Statistics
              </Typography>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <Typography sx={{ fontSize: 16 }}><strong>Total Bets (USDT):</strong> {bettingStats.USDT.total_bet.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: 16 }}><strong>Total Wins (USDT):</strong> {bettingStats.USDT.total_win.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: 16 }}><strong>Total Losses (USDT):</strong> {bettingStats.USDT.total_loss.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: 16 }}><strong>Total Bets (INR):</strong> {bettingStats.INR.total_bet.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: 16 }}><strong>Total Wins (INR):</strong> {bettingStats.INR.total_win.toFixed(2)}</Typography>
                <Typography sx={{ fontSize: 16 }}><strong>Total Losses (INR):</strong> {bettingStats.INR.total_loss.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ mt: 4, maxWidth: 400, mx: 'auto' }}>
                <Pie 
                  data={bettingStatsChartData} 
                  options={{ 
                    responsive: true, 
                    plugins: { 
                      legend: { position: 'top' },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            const label = context.label || '';
                            const value = context.parsed || 0;
                            return `${label}: ${value.toFixed(2)}`;
                          }
                        }
                      }
                    } 
                  }} 
                />
              </Box>
            </CardContent>
          </StyledCard>
        )}

        {/* Users Table */}
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, fontSize: { xs: 18, sm: 20 }, color: '#1a237e' }}>
              Users
            </Typography>
         
            {/* Add this right before or after the existing FormControl for user filtering */}
<Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
  <FormControl sx={{ minWidth: 200 }}>
    <InputLabel>Filter by User</InputLabel>
    <Select value={selectedUserId} label="Filter by User" onChange={handleUserFilterChange}>
      <MenuItem value="">All Users</MenuItem>
      {clientData?.users?.map((user:any) => (
        <MenuItem key={user.id} value={user.id}>
          {user.username} (ID: {user.user_id})
        </MenuItem>
      ))}
    </Select>
  </FormControl>
  
  {/* This is the new search field */}
  <TextField
    label="Search by username or ID"
    variant="outlined"
    value={searchTerm}
    onChange={handleSearchChange}
    sx={{ 
      minWidth: 250,
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
      }
    }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          {searchTerm && (
            <IconButton
              aria-label="clear search"
              onClick={() => setSearchTerm('')}
              edge="end"
            >
              <CloseIcon />
            </IconButton>
          )}
        </InputAdornment>
      ),
    }}
  />
</Box>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <StyledTableRow>
                    {/* <StyledTableCell>ID</StyledTableCell> */}
                    <StyledTableCell>User ID</StyledTableCell>
                    <StyledTableCell>Username</StyledTableCell>
                    <StyledTableCell>Balance USDT</StyledTableCell>
                    <StyledTableCell>Balance INR</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  {clientData?.users
  ?.filter((user:any) => 
    (!selectedUserId || user.id === selectedUserId) && 
    (searchTerm === '' || 
     user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.user_id.toString().includes(searchTerm))
  )
  ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  ?.map((user: any) => (
                      <React.Fragment key={user.id}>
                        <Fade in timeout={500}>
                          <StyledTableRow>
                            {/* <StyledTableCell>{user.id}</StyledTableCell> */}
                            <StyledTableCell>{user.user_id}</StyledTableCell>
                            <StyledTableCell>{user.username}</StyledTableCell>
                            <StyledTableCell>{user.balance} USDT</StyledTableCell>
                            <StyledTableCell>{user.inr_balance} INR</StyledTableCell>
                            <StyledTableCell>
                              <Button
                                onClick={() => navigate(`/client/user/${user.id}/history`)}
                                variant="contained"
                                size="small"
                                sx={{ borderRadius: 1, textTransform: 'none', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, px: 2, py: 1, mr: 1 }}
                              >
                                View History
                              </Button>
                              <ExpandButton
                                onClick={() => toggleUserTransactions(user.id)}
                                endIcon={<ExpandMoreIcon sx={{ transform: expandedUserId === user.id ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
                                size="small"
                              >
                                {expandedUserId === user.id ? 'Hide' : 'Show'} Transactions
                              </ExpandButton>
                            </StyledTableCell>
                          </StyledTableRow>
                        </Fade>
                        <StyledTableRow>
                          <StyledTableCell colSpan={6} style={{ padding: 0 }}>
                            <Collapse in={expandedUserId === user.id}>
                              <Box sx={{ p: 2 }}>
                                <Box sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
                                  <Line 
                                    data={getUserBalanceChartData(user.id)} 
                                    options={{ 
                                      responsive: true, 
                                      plugins: { 
                                        legend: { position: 'top' },
                                        tooltip: {
                                          callbacks: {
                                            label: (context) => {
                                              const label = context.dataset.label || '';
                                              const value = context.parsed.y || 0;
                                              return `${label}: ${value.toFixed(2)}`;
                                            }
                                          }
                                        }
                                      } 
                                    }} 
                                  />
                                </Box>
                                <Table sx={{ minWidth: 650 }}>
                                  <TableHead>
                                    <StyledTableRow>
                                      {/* <StyledTableCell>ID</StyledTableCell> */}
                                      <StyledTableCell>Transaction Type</StyledTableCell>
                                      <StyledTableCell>Amount</StyledTableCell>
                                      <StyledTableCell>Balance Before</StyledTableCell>
                                      <StyledTableCell>Balance After</StyledTableCell>
                                      <StyledTableCell>Description</StyledTableCell>
                                      <StyledTableCell>Date</StyledTableCell>
                                    </StyledTableRow>
                                  </TableHead>
                                  <TableBody>
                                    {userTransactions[user.id]?.map((tx: any) => (
                                      <Fade in key={tx.id} timeout={500}>
                                        <StyledTableRow>
                                          {/* <StyledTableCell>{tx.id}</StyledTableCell> */}
                                          <StyledTableCell>{tx.transaction_type}</StyledTableCell>
                                          <StyledTableCell>{tx.amount} {tx.currency}</StyledTableCell>
                                          <StyledTableCell>{tx.balance_before}</StyledTableCell>
                                          <StyledTableCell>{tx.balance_after}</StyledTableCell>
                                          <StyledTableCell>{tx.description}</StyledTableCell>
                                          <StyledTableCell>{new Date(tx.created_at).toLocaleString()}</StyledTableCell>
                                        </StyledTableRow>
                                      </Fade>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </StyledTableCell>
                        </StyledTableRow>
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
count={clientData?.users?.filter((user:any) => 
  (!selectedUserId || user.id === selectedUserId) && 
  (searchTerm === '' || 
   user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
   user.user_id.toString().includes(searchTerm))
).length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </StyledCard>

        {/* Client Balance Transactions */}
        <StyledCard>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: 18, sm: 20 }, color: '#1a237e' }}>
                Client Balance Transactions
              </Typography>
              <ExpandButton
                onClick={() => setShowClientTransactions(!showClientTransactions)}
                endIcon={<ExpandMoreIcon sx={{ transform: showClientTransactions ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
              >
                {showClientTransactions ? 'Hide' : 'Show'} Transactions
              </ExpandButton>
            </Box>
            <Collapse in={showClientTransactions}>
              <Box sx={{ mt: 4, maxWidth: 600, mx: 'auto', mb: 4 }}>
                <Line 
                  data={clientBalanceChartData} 
                  options={{ 
                    responsive: true, 
                    plugins: { 
                      legend: { position: 'top' },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            const label = context.dataset.label || '';
                            const value = context.parsed.y || 0;
                            return `${label}: ${value.toFixed(2)}`;
                          }
                        }
                      }
                    } 
                  }} 
                />
              </Box>
              <TableContainer>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <StyledTableRow>
                      {/* <StyledTableCell>ID</StyledTableCell> */}
                      <StyledTableCell>Transaction Type</StyledTableCell>
                      {/* <StyledTableCell>Amount</StyledTableCell>
                      <StyledTableCell>GGR Amount</StyledTableCell> */}
                      <StyledTableCell>Balance Before</StyledTableCell>
                      <StyledTableCell>Balance After</StyledTableCell>
                      <StyledTableCell>Description</StyledTableCell>
                      <StyledTableCell>Created By</StyledTableCell>
                      <StyledTableCell>Date</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {clientTransactions
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((tx: any) => (
                        <Fade in key={tx.id} timeout={500}>
                          <StyledTableRow>
                            {/* <StyledTableCell>{tx.id}</StyledTableCell> */}
                            <StyledTableCell>{tx.transaction_type}</StyledTableCell>
                            {/* <StyledTableCell>{tx.amount} USD</StyledTableCell>
                            <StyledTableCell>{(tx.amount * 12 / 100)} USD</StyledTableCell> */}
                            <StyledTableCell>{tx.balance_before}</StyledTableCell>
                            <StyledTableCell>{tx.balance_after}</StyledTableCell>
                            <StyledTableCell>{tx.description}</StyledTableCell>
                            <StyledTableCell>{tx.created_by}</StyledTableCell>
                            <StyledTableCell>{new Date(tx.created_at).toLocaleString()}</StyledTableCell>
                          </StyledTableRow>
                        </Fade>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={clientTransactions.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Collapse>
          </CardContent>
        </StyledCard>
      </Box>
    </Box>
  );
};

export default ClientView;